import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const COLORS = ["#0A192F", "#FF3B3B", "#00B8D4", "#16A34A", "#F59E0B"];
const MAX_PTS = 280;
const newCid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Shared whiteboard: strokes are stored as normalised points (0-1), so every screen size draws the same picture.
export const GroupBoard = ({ events, onStroke, onClear, readOnly = false }) => {
  const { L } = useLang();
  const cv = useRef(null);
  const cur = useRef(null);
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(3);
  const [pending, setPending] = useState([]);
  const strokes = useMemo(() => {
    let from = 0;
    events.forEach((e, i) => { if (e.kind === "clear") from = i + 1; });
    return events.slice(from).filter((e) => e.kind === "stroke");
  }, [events]);
  const visible = useMemo(() => {
    const have = new Set(events.filter((e) => e.kind === "stroke").map((s) => s.cid)); // any synced stroke (even before a clear) is no longer pending
    return [...strokes, ...pending.filter((p) => !have.has(p.cid))];
  }, [events, strokes, pending]);

  useEffect(() => { // drop pending strokes once the server has them
    const have = new Set(events.filter((e) => e.kind === "stroke").map((s) => s.cid));
    setPending((p) => (p.some((x) => have.has(x.cid)) ? p.filter((x) => !have.has(x.cid)) : p));
  }, [events]);
  const clearAll = () => { setPending([]); onClear(); };

  const draw = useCallback(() => {
    const c = cv.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== Math.round(r.width * dpr)) { c.width = Math.round(r.width * dpr); c.height = Math.round(r.height * dpr); }
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, r.width, r.height);
    const line = (s) => {
      ctx.strokeStyle = s.color; ctx.lineWidth = s.width; ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath();
      s.points.forEach(([x, y], i) => (i ? ctx.lineTo(x * r.width, y * r.height) : ctx.moveTo(x * r.width, y * r.height)));
      if (s.points.length === 1) ctx.lineTo(s.points[0][0] * r.width + 0.1, s.points[0][1] * r.height);
      ctx.stroke();
    };
    visible.forEach(line);
    if (cur.current) line(cur.current);
  }, [visible]);
  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const ro = new ResizeObserver(() => draw());
    ro.observe(cv.current);
    return () => ro.disconnect();
  }, [draw]);

  const pos = (e) => {
    const r = cv.current.getBoundingClientRect();
    return [Math.min(1, Math.max(0, +((e.clientX - r.left) / r.width).toFixed(4))), Math.min(1, Math.max(0, +((e.clientY - r.top) / r.height).toFixed(4)))];
  };
  const send = (s) => {
    setPending((x) => [...x, s]);
    Promise.resolve(onStroke(s)).catch(() => setPending((x) => x.filter((y) => y.cid !== s.cid)));
  };
  const down = (e) => {
    if (readOnly) return;
    cv.current.setPointerCapture(e.pointerId);
    cur.current = { cid: newCid(), color, width, points: [pos(e)] };
    draw();
  };
  const move = (e) => {
    const s = cur.current;
    if (!s) return;
    const p = pos(e);
    const q = s.points[s.points.length - 1];
    if (Math.hypot(p[0] - q[0], p[1] - q[1]) < 0.004) return;
    s.points.push(p);
    if (s.points.length >= MAX_PTS) { send(s); cur.current = { cid: newCid(), color, width, points: [p] }; }
    draw();
  };
  const up = () => { const s = cur.current; cur.current = null; if (s) send(s); };

  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-3" data-testid="group-board">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="board-toolbar">
          {COLORS.map((c, i) => <button key={c} onClick={() => setColor(c)} aria-label={`Colour ${i + 1}`} data-testid={`board-color-${i}`}
            className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c ? "border-[#0A192F] scale-110" : "border-white"}`} style={{ background: c }} />)}
          <span className="w-px h-6 bg-slate-200 mx-1" />
          {[[3, "Thin", "पतला"], [7, "Thick", "मोटा"]].map(([w, en, h]) => <button key={w} onClick={() => setWidth(w)} data-testid={`board-width-${w}`}
            className={`h-8 px-3 rounded-full text-xs font-bold border inline-flex items-center gap-1 ${width === w ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300"}`}><PenLine className="w-3.5 h-3.5" />{L(en, h)}</button>)}
          <button onClick={clearAll} data-testid="board-clear-btn" className="ml-auto h-8 px-3 rounded-full text-xs font-bold border border-slate-300 inline-flex items-center gap-1 hover:border-[#FF3B3B]"><Eraser className="w-3.5 h-3.5" />{L("Clear board", "बोर्ड साफ़")}</button>
        </div>
      )}
      <canvas ref={cv} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="board-canvas" data-strokes={visible.length}
        className={`w-full aspect-[16/10] rounded-2xl border border-slate-100 touch-none ${readOnly ? "cursor-default" : "cursor-crosshair"}`}
        style={{ backgroundImage: "radial-gradient(#D7E3F4 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
    </div>
  );
};
